import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SearchBar from './SearchBar';
import TagFilter from './TagFilter';
import { paperApi } from '../utils/apiUtils';
import PaperCollection from './PaperCollection';
import { filterPapers } from '../utils/paperUtils';

function Today() {
  const location = useLocation();
  // `?q=<term>` comes from the home-page hero / cmd+k modal when the user
  // enters keyword mode. We seed the initial search with that value so the
  // handoff from those surfaces feels like a real search, not a blank page.
  const initialQuery = new URLSearchParams(location.search).get('q') || '';

  const [papers, setPapers] = useState([]);
  const [filteredPapers, setFilteredPapers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    const getPapers = async () => {
      try {
        const { papers } = await paperApi.fetchPapersToday();
        setPapers(papers);
        setFilteredPapers(filterPapers(papers, initialQuery, null));
      } catch (error) {
        console.error('Error fetching papers:', error);
      }
    };
    getPapers();
  }, [initialQuery]);

  // Re-sync parent searchTerm when the URL's ?q= changes (e.g. user returns
  // from the home hero with a new keyword). Without this, filtering and tag
  // selection would keep using a stale term.
  useEffect(() => {
    setSearchTerm(initialQuery);
  }, [initialQuery]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setFilteredPapers(filterPapers(papers, term, selectedTag));
  };

  const handleTagSelect = useCallback((tag) => {
    setSelectedTag(tag);
    setFilteredPapers(filterPapers(papers, searchTerm, tag));
  }, [papers, searchTerm]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-7xl mx-auto"
    >
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-yellow-300/60 text-xs">{'>'}</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Today's Papers</h1>
          {papers.length > 0 && (
            <span className="text-xs text-white/30 font-semibold tabular-nums">
              [{filteredPapers.length}]
            </span>
          )}
        </div>
        <div className="h-px bg-white/10 mt-3" />
      </div>

      <div className="space-y-4 mb-8">
        <SearchBar onSearch={handleSearch} initialValue={initialQuery} />
        <TagFilter onTagSelect={handleTagSelect} />
      </div>
      <PaperCollection papers={filteredPapers} />
    </motion.div>
  );
}

export default Today;
